import { useEffect, useRef, type RefObject } from 'react'
import { useLocation } from 'react-router'
import { POST_PAYMENT_URL } from '../../config/config'
import { PAYMENT_INTENT_PARAM } from './completingOrderSaver'

interface PostRedirectPaymentHandler {
  handlePostRedirectPayment: (paymentIntentId: string) => void
}

/**
 * After a redirect-based payment, PostPaymentCompletingOrder forwards us off the
 * blank /post-payment page back to the product page with `payment_intent` in the
 * URL. This calls CompletingOrder's handler (via its ref) exactly once we've
 * landed there — never while still on /post-payment — so completion can't race
 * the redirect. Reacts to the navigation instead of polling for it.
 */
export function useHandlePostRedirectPayment(
  completingOrderRef: RefObject<PostRedirectPaymentHandler | null>
) {
  const location = useLocation()
  const handledPaymentIntentId = useRef<string | null>(null)

  useEffect(() => {
    if (location.pathname === POST_PAYMENT_URL) return
    const paymentIntentId = new URLSearchParams(location.search).get(
      PAYMENT_INTENT_PARAM
    )
    if (
      !paymentIntentId ||
      handledPaymentIntentId.current === paymentIntentId
    ) {
      return
    }
    handledPaymentIntentId.current = paymentIntentId
    completingOrderRef.current?.handlePostRedirectPayment(paymentIntentId)
  }, [location.pathname, location.search, completingOrderRef])
}
